import React from "react";
import { SpaceBetweenAlignContent } from "../CustomAntdComponents/Utitlities";
import {
  StyledParagraph,
  StyledTitle,
} from "../CustomAntdComponents/Typography";
import { passengerCategoryLabel } from "../PassengerFormItem/helpers";
import { formatNumberAsCurrency } from "../../utils/helpers";
import { BreakdownWrapper, CreditAppliedRow } from "./styles";
import {
  calculateFlightFareBreakdown,
  calculateFlightTotalFare,
  calculateTicketTotal,
  calculateTicketTotalOldPrice,
  calculateTicketTotalDiscountPrice,
} from "../AvailableFlights/helper";
import { useFlightSearchStore } from "../../store/flightSearch/flightSearchStore";
import { useBookingStore } from "../../store/booking/bookingStore";
import { TripLabel } from "../RoadItineraryBreakdown/styles";
import { StrikeThroughAmount } from "../AvailableFlights/styles";
import { useI18nContext } from "../../utils/I18n";
import translations from "../../screens/PassengerDetails/translations";
import { useAuthStore } from "../../store/auth/authStore";

type Props = {
  hideBreakdown: boolean;
  hideCreditAppliedMsg?: boolean;
};
const FlightFareBreakdown = ({ hideBreakdown, hideCreditAppliedMsg }: Props) => {
  const {
    selectedFlight,
    formData: { isDomestic },
  } = useFlightSearchStore();

  const {
    state: { t },
  } = useI18nContext();

  if (!selectedFlight?.length) {
    return null;
  }

  const selectedFlightFirst = selectedFlight[0];
  const { bookingPayload, confirmData } = useBookingStore();
  const { totalTicketAmount, totalOriginalPrice } = calculateFlightTotalFare(
    selectedFlight,
    isDomestic
  );
  const departureFares = calculateFlightFareBreakdown([selectedFlightFirst]);
  const returnFares =
    isDomestic && selectedFlight.length > 1
      ? calculateFlightFareBreakdown([selectedFlight[1]])
      : null;
  const bookingAmount = bookingPayload
    ? calculateTicketTotal(bookingPayload.TicketingDetails)
    : totalTicketAmount;

  const totalOldPrice = confirmData
    ? calculateTicketTotalOldPrice(confirmData)
    : 0;

  const discountAmount = confirmData
    ? calculateTicketTotalDiscountPrice(confirmData)
    : 0;

  const { creditApplied, b2CUser, creditAmount } = useAuthStore();
  let displayAmount = bookingAmount;
  let creditDeducted = 0;
  if (creditApplied && creditAmount > 0) {
    creditDeducted = Math.min(creditAmount, bookingAmount);
    displayAmount = bookingAmount - creditDeducted;
  }

  return (
    <div>
      {!hideBreakdown && (
        <BreakdownWrapper>
          {returnFares && <TripLabel size="normal">Departure</TripLabel>}
          {departureFares.map((f, key) => (
            <SpaceBetweenAlignContent key={key}>
              <StyledParagraph style={{ fontSize: "14px" }}>
                {passengerCategoryLabel(f.PassengerType)} &nbsp;x&nbsp;{" "}
                {f.Quantity}
              </StyledParagraph>

              <StyledTitle level={5} style={{ color: "#434455" }}>
                {formatNumberAsCurrency(
                  f.TotalFare * f.Quantity || 0,
                  selectedFlight[0]?.Currency
                )}
              </StyledTitle>
            </SpaceBetweenAlignContent>
          ))}

          {returnFares && (
            <div style={{ marginTop: "10px" }}>
              <TripLabel size="normal">Return</TripLabel>
              {returnFares.map((f, key) => (
                <SpaceBetweenAlignContent key={key}>
                  <StyledParagraph style={{ fontSize: "14px" }}>
                    {passengerCategoryLabel(f.PassengerType)} &nbsp;x&nbsp;{" "}
                    {f.Quantity}
                  </StyledParagraph>

                  <StyledTitle level={5} style={{ color: "#434455" }}>
                    {formatNumberAsCurrency(
                      f.TotalFare * f.Quantity || 0,
                      selectedFlight[0]?.Currency
                    )}
                  </StyledTitle>
                </SpaceBetweenAlignContent>
              ))}
            </div>
          )}

          {!hideCreditAppliedMsg && creditApplied && creditDeducted > 0 && (
            <CreditAppliedRow>
              Credit applied (  {formatNumberAsCurrency(creditDeducted, "")} off)
            </CreditAppliedRow>
          )}
        </BreakdownWrapper>
      )}

      <SpaceBetweenAlignContent>
        {confirmData?.length && confirmData[0]?.IsDiscounted ? (
          <p className="total-passengers-text">Total for all passengers</p>
        ) : (
          <StyledParagraph size="default">
            Total for all passengers
          </StyledParagraph>
        )}

        <div className="make-payment-amount-wrapper" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {creditApplied && creditDeducted > 0 ? (
            <StrikeThroughAmount>
              {formatNumberAsCurrency(bookingAmount, selectedFlight[0]?.Currency)}
            </StrikeThroughAmount>
          ) : confirmData?.length && confirmData[0]?.IsDiscounted ? (
            <StrikeThroughAmount>
              {formatNumberAsCurrency(
                bookingAmount + discountAmount,
                selectedFlight[0]?.Currency
              )}
            </StrikeThroughAmount>
          ) : selectedFlight[0].IsDiscounted ? (
            <StrikeThroughAmount>
              {formatNumberAsCurrency(
                totalOriginalPrice ?? 0,
                selectedFlight[0]?.Currency
              )}
            </StrikeThroughAmount>
          ) : null}
          <StyledTitle level={3}>
            {formatNumberAsCurrency(
              displayAmount || 0,
              selectedFlight[0]?.Currency
            )}
          </StyledTitle>
        </div>
      </SpaceBetweenAlignContent>

      {confirmData?.length && confirmData[0]?.IsDiscounted ? (
        <div className="discount-container-desktop ">
          <span>{t(translations).discountApplied}</span>
          <span className="discount-code-text">
            {confirmData[0]?.PassengerDetails?.PromoCode}
          </span>
          <span className="discount-amount-off-text">
            (
            {formatNumberAsCurrency(
              discountAmount,
              selectedFlight[0]?.Currency
            )}{" "}
            {t(translations).off})
          </span>
        </div>
      ) : null}
    </div>
  );
};

export default FlightFareBreakdown;
